import { Button, Modal } from '@ixicare/ui';
import * as React from 'react';
import AlertResolveModal from './AlertResolveModal';
import { observer } from 'mobx-react';
import Alert from '~store/Alert/Alert';
import { t } from '~i18n';
import styles from './AlertActions.module.css';
import { AlertStatus, Profile } from '@ixicare/resources';
import AlertRejectModal from './AlertRejectModal';
import { useQuery } from '@tanstack/react-query';
import classnames from 'classnames';
import AlertCallModal from './AlertCallModal';
import { useStores } from '~store/utils';
import { StartHandlingHereButton } from '~components/Presence';
import { useObserverAlertMode } from '~hooks/useObserverAlertMode';
import { UserAction } from '~config/toastNotification';
import { ClipboardCheck } from 'lucide-react';

interface AlertActionsContainerProps {
  alert: Alert;
}

type ActionType = 'resolve' | 'reject' | 'call';

const AlertActionsContainer: React.FC<AlertActionsContainerProps> = ({ alert }) => {
  const { auth, notifications, permissions } = useStores();
  const { isObserver, isActiveObserver, isPassiveObserver, isOnDutyElsewhere, onDutySiteName, selectedSiteUuid } =
    useObserverAlertMode();

  let isReadOnly: boolean;
  if (isObserver) {
    // Observers act only while active, and only on their group's alarms at the
    // active (selected) site. Passive observers are read-only everywhere.
    isReadOnly = !isActiveObserver || alert.siteUuid !== selectedSiteUuid;
  } else {
    // Multi-site viewers (can_view_site) can browse other sites' alarms but may only
    // act on their own site. A single-site profile keeps full access on its own site.
    const isRemoteViewerType = permissions.has('can_view_site');
    // siteUuid is null until the detail fetch lands — treat "unknown" as not-own-site (fail safe to read-only).
    const isOwnSite = !!alert.siteUuid && alert.siteUuid === auth.user?.site?.uuid;
    isReadOnly = isRemoteViewerType && !isOwnSite;
  }

  // A passive observer's action buttons stay enabled but explain (rather than
  // silently do nothing) that they must start handling alarms first.
  const [showPassiveInfo, setShowPassiveInfo] = React.useState(false);

  // Dismiss the explainer the moment they start handling alarms from inside it —
  // the reason it was open no longer applies.
  React.useEffect(() => {
    if (showPassiveInfo && !isPassiveObserver) setShowPassiveInfo(false);
  }, [showPassiveInfo, isPassiveObserver]);
  const actionsDisabled = isReadOnly && !isPassiveObserver;
  const guard = (run: () => void) => () => {
    if (isPassiveObserver) {
      setShowPassiveInfo(true);
      return;
    }
    run();
  };

  const [actionType, setActionType] = React.useState<ActionType>();
  const [loading, setLoading] = React.useState(false);
  // The store flips the status to Accepted before the request lands, which would turn this
  // button into Resolve while its own spinner is still running. Tracked separately from
  // `loading` so that resolving — which also sets loading — never swaps the button back.
  const [accepting, setAccepting] = React.useState(false);

  const notifyActionError = React.useCallback(
    (err: any) => {
      console.error(err);
      notifications.addUserActionNotification({
        type: UserAction.AlarmAction,
        notificationType: 'error',
        message: t('alert.actions.error'),
      });
    },
    [notifications]
  );

  const handleReject = async (reason: string) => {
    try {
      setLoading(true);
      await alert.reject(reason);
      setActionType(undefined);
    } catch (err) {
      notifyActionError(err);
    }
    setLoading(false);
  };

  const handleAccept = async () => {
    try {
      setLoading(true);
      setAccepting(true);
      await alert.accept();
      setActionType(undefined);
    } catch (err) {
      notifyActionError(err);
    }
    setLoading(false);
    setAccepting(false);
  };

  const handleResolve = async (reason: string) => {
    try {
      setLoading(true);
      await alert.resolve(reason);
      setActionType(undefined);
    } catch (err) {
      notifyActionError(err);
    }
    setLoading(false);
  };

  const { data: resident } = useQuery({
    queryKey: ['profile', alert.resident?.id],
    queryFn: () => Profile.getProfile(alert.resident?.id ?? -1),
    enabled: !!alert.resident?.id,
  });

  const phoneNumbers = React.useMemo(() => {
    const data: { label: string; value: string }[] = [];
    if (alert.device?.phoneNumber) {
      data.push({ label: t('alert.call.devicePhoneNumber'), value: alert.device?.phoneNumber });
    }
    if (resident?.phoneNumber) {
      data.push({ label: t('alert.call.residentPrimaryPhoneNumber'), value: resident.phoneNumber });
    }
    if (resident?.secondaryPhoneNumbers) {
      data.push({ label: t('alert.call.residentSecondaryPhoneNumber'), value: resident.secondaryPhoneNumbers });
    }
    return data;
  }, [alert, resident]);
  const canCall = alert.isOpen && phoneNumbers.length;

  const handleCall = React.useCallback(() => {
    if (!alert.isAccepted) {
      alert.accept();
    }
    alert.allowUserToResolve();
  }, [alert]);

  // Resolved and cancelled are terminal: no user, on any site, in any mode, can act on the
  // alarm again. The page is a history record, so it gets no action bar rather than a dead one.
  if (alert.isResolved) return null;

  return (
    <>
      <div className={styles.AlertActions}>
        <Button
          className={styles.ActionButton}
          disabled={actionsDisabled || alert.isRejected || alert.status !== AlertStatus.Open}
          loading={loading}
          onClick={guard(() => setActionType('reject'))}
          buttonType="error"
          icon="cross"
        >
          {t('alert.actions.decline')}
        </Button>
        <div style={{ display: 'flex' }}>
          <Button
            className={classnames(styles.ActionButton, 'mr-5')}
            disabled={actionsDisabled || !canCall}
            onClick={guard(() => setActionType('call'))}
            icon="voice-communication"
            buttonType="inverse"
          >
            {t('alert.actions.call')}
          </Button>
          {alert.status === AlertStatus.Accepted && !accepting ? (
            <Button
              className={styles.ActionButton}
              buttonType="success"
              disabled={actionsDisabled}
              loading={loading}
              onClick={guard(() => setActionType('resolve'))}
              lucide={ClipboardCheck}
            >
              {t('alert.actions.resolve')}
            </Button>
          ) : (
            <Button
              className={styles.ActionButton}
              buttonType="success"
              disabled={actionsDisabled || alert.status !== AlertStatus.Open}
              loading={loading}
              onClick={guard(handleAccept)}
              icon="check"
            >
              {t('alert.actions.accept')}
            </Button>
          )}
        </div>
      </div>

      {showPassiveInfo && (
        <Modal onClose={() => setShowPassiveInfo(false)}>
          <h3>{t('alertMode.passiveModal.title')}</h3>
          {/* Passive covers two different situations, and "start handling
              alarms below" is only the way out of one of them: someone already
              on duty at another site has no start button here, just a stop. */}
          <p className="mb-20">
            {isOnDutyElsewhere
              ? t('alertMode.passiveModal.bodyOnDutyElsewhere', { site: onDutySiteName ?? '' })
              : t('alertMode.passiveModal.body')}
          </p>
          {/* The real control, not a picture of one — so the explanation and the
              way out of view-only are the same thing. Just the one action,
              though: the duty pill answers "where am I on duty", which is what
              the text above already says and what made this read oddly. */}
          <StartHandlingHereButton />
        </Modal>
      )}

      {actionType === 'resolve' && (
        <AlertResolveModal
          alert={alert}
          onClose={() => setActionType(undefined)}
          onResolve={handleResolve}
          loading={loading}
        />
      )}
      {actionType === 'reject' && (
        <AlertRejectModal onClose={() => setActionType(undefined)} onReject={handleReject} loading={loading} />
      )}
      {actionType === 'call' && (
        <AlertCallModal phoneNumbers={phoneNumbers} onClose={() => setActionType(undefined)} onCall={handleCall} />
      )}
    </>
  );
};

export default observer(AlertActionsContainer);
